import { KeyValueStore } from '@child-nav/shared';
import { get, set, del, keys as idbKeys } from 'idb-keyval';

export class WebStorageAdapter implements KeyValueStore {
  async getItem(key: string): Promise<string | null> {
    try {
      const value = await get(key);
      if (value != null) return value;
      // Key not in IndexedDB — also check localStorage (auth tokens written
      // there directly by WebAuthService bypass IndexedDB entirely).
      return localStorage.getItem(key);
    } catch (error) {
      console.error('WebStorageAdapter.getItem error:', error);
      // Fallback to localStorage
      try {
        return localStorage.getItem(key);
      } catch (fallbackError) {
        console.error('localStorage fallback error:', fallbackError);
        return null;
      }
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      await set(key, value);
    } catch (error) {
      console.error('WebStorageAdapter.setItem error:', error);
      // Fallback to localStorage
      try {
        localStorage.setItem(key, value);
      } catch (fallbackError) {
        console.error('localStorage fallback error:', fallbackError);
        throw fallbackError;
      }
    }
  }

  async removeItem(key: string): Promise<void> {
    // Remove from IndexedDB — this is the primary store
    try {
      await del(key);
    } catch (error) {
      console.error('WebStorageAdapter.removeItem (IndexedDB) error:', error);
    }
    // Always also remove from localStorage: setItem falls back to localStorage when
    // IndexedDB is unavailable, so data may have been written there instead.  Omitting
    // this step was the root cause of DebugView "Clear All Admins" appearing to have
    // no effect — items stored via the fallback path were never actually removed.
    try {
      localStorage.removeItem(key);
    } catch (fallbackError) {
      console.error('WebStorageAdapter.removeItem (localStorage) error:', fallbackError);
    }
  }

  // Helper methods for JSON serialization
  async getJSON<T = any>(key: string): Promise<T | null> {
    try {
      const value = await this.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error('WebStorageAdapter.getJSON error:', error);
      return null;
    }
  }

  async setJSON<T = any>(key: string, value: T): Promise<void> {
    try {
      await this.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('WebStorageAdapter.setJSON error:', error);
      throw error;
    }
  }

  // Web-specific methods
  async getAllKeys(): Promise<string[]> {
    try {
      // Enumerate both stores and union the key sets so callers see every persisted key.
      // Previously only localStorage was enumerated, which caused IndexedDB-stored keys
      // (e.g. admin_state_*) to be invisible to getAllKeys().
      const idbKeyList = await idbKeys() as string[];
      const localStorageKeys = Object.keys(localStorage);
      return Array.from(new Set([...idbKeyList, ...localStorageKeys]));
    } catch (error) {
      console.error('WebStorageAdapter.getAllKeys error:', error);
      // Fall back to localStorage only if IndexedDB enumeration fails
      try {
        return Object.keys(localStorage);
      } catch {
        return [];
      }
    }
  }

  async multiGet(keys: string[]): Promise<Array<[string, string | null]>> {
    try {
      const results = await Promise.all(
        keys.map(async (key) => {
          const value = await this.getItem(key);
          return [key, value] as [string, string | null];
        })
      );
      return results;
    } catch (error) {
      console.error('WebStorageAdapter.multiGet error:', error);
      return keys.map(key => [key, null]);
    }
  }

  async multiSet(keyValuePairs: Array<[string, string]>): Promise<void> {
    try {
      await Promise.all(
        keyValuePairs.map(([key, value]) => this.setItem(key, value))
      );
    } catch (error) {
      console.error('WebStorageAdapter.multiSet error:', error);
      throw error;
    }
  }

  async multiRemove(keys: string[]): Promise<void> {
    try {
      await Promise.all(
        keys.map(key => this.removeItem(key))
      );
    } catch (error) {
      console.error('WebStorageAdapter.multiRemove error:', error);
      throw error;
    }
  }

  // Clear all data (use with caution)
  async clear(): Promise<void> {
    try {
      // Clear IndexedDB
      const keys = await this.getAllKeys();
      await Promise.all(keys.map(key => this.removeItem(key)));
      
      // Clear localStorage as fallback
      localStorage.clear();
    } catch (error) {
      console.error('WebStorageAdapter.clear error:', error);
      throw error;
    }
  }
}

// Singleton instance
export const webStorageAdapter = new WebStorageAdapter();